import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getSeoForPath, type SeoMetadata } from '../lib/seo';

interface SeoHeadProps {
  /**
   * Optional: Override the auto-detected SEO metadata
   * If not provided, metadata will be determined from the current route
   */
  override?: Partial<SeoMetadata>;
  /**
   * Optional: Mark the page as noindex
   * Useful for authenticated pages or pages that should not be indexed
   */
  noIndex?: boolean;
}

/**
 * SeoHead component
 *
 * Manages SEO metadata for the current route using react-helmet-async.
 * Automatically determines the correct metadata based on the current pathname,
 * or accepts an override for custom pages.
 *
 * Usage:
 *   <SeoHead />  // Auto-detect from route
 *   <SeoHead override={{ title: "Custom Title" }} />  // Override specific fields
 *   <SeoHead noIndex />  // Mark as noindex
 */
export default function SeoHead({ override, noIndex }: SeoHeadProps) {
  const location = useLocation();

  // Get base SEO from route
  const baseSeo = getSeoForPath(location.pathname);

  // Merge with any overrides
  const seo: SeoMetadata = {
    ...baseSeo,
    ...override,
  };

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{seo.title}</title>
      <meta name='description' content={seo.description} />
      <link rel='canonical' href={seo.url} />

      {/* Robots directive */}
      {noIndex && <meta name='robots' content='noindex, nofollow' />}

      {/* Open Graph tags */}
      <meta property='og:type' content={seo.type} />
      <meta property='og:url' content={seo.url} />
      <meta property='og:title' content={seo.title} />
      <meta property='og:description' content={seo.description} />
      <meta property='og:image' content={seo.image} />
      <meta property='og:image:width' content='1200' />
      <meta property='og:image:height' content='630' />
      {seo.siteName && <meta property='og:site_name' content={seo.siteName} />}
      <meta property='og:locale' content='en_US' />

      {/* Twitter Card tags */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:site' content='@layerre' />
      <meta name='twitter:creator' content='@layerre' />
      <meta name='twitter:title' content={seo.title} />
      <meta name='twitter:description' content={seo.description} />
      <meta name='twitter:image' content={seo.image} />
      <meta name='twitter:image:alt' content={seo.title} />
      <meta name='twitter:domain' content='layerre.com' />
      <meta name='twitter:url' content={seo.url} />
    </Helmet>
  );
}
